import {FULFILLED} from 'redux-promise-middleware';
import {createAction, createReducer} from 'redux-act';
import {omit, equals, pathOr} from 'ramda';

import {SEND_LATER, UPLOAD, FILL_IT, NOT_SELECTED} from 'constants/wizard/prescriptionType';
import {
    CODE_FILE_UNDEFINED_ERROR,
    CODE_FILE_SIZE_ERROR,
    CODE_FILE_TYPE_ERROR,
} from 'constants/file/fileCode';
import {ApplicationStoreType} from 'reducers';
import {AxiosResponse} from 'axios';
import {OneQuoteItem} from 'constants/flow/flowTypesCart';
import {getContactsPdp} from 'features/contacts/selectors/contacts/contacts';
import {fetchQuoteItemV2} from 'actions/cart/cart';

export type MethodType = typeof SEND_LATER | typeof UPLOAD | typeof FILL_IT | typeof NOT_SELECTED;

export type ErrorType =
    | typeof CODE_FILE_UNDEFINED_ERROR
    | typeof CODE_FILE_SIZE_ERROR
    | typeof CODE_FILE_TYPE_ERROR
    | ''; // @skokalin TODO investigate and fix

export type UploadedDataType = {
    type: 'success' | 'error';
    fileSrc?: string;
    fileName?: string;
    errorType?: ErrorType;
    fileType: string;
};

export type PrescriptionType = {
    method: MethodType;
    isSamePrescriptionForBothEyes: boolean;
    uploadedData: UploadedDataType;
};

export const initialState: PrescriptionType = {
    method: SEND_LATER,
    isSamePrescriptionForBothEyes: false,
    uploadedData: {
        type: 'success',
        fileType: '',
    },
};

/**
 * List of actions
 */
export const changePrescriptionMethod = createAction<MethodType>(
    'CONTACTS_WIZARD_SET_PRESCRIPTION_METHOD',
);

export const toggleSamePrescriptionForBothEyes = createAction(
    'CONTACTS_WIZARD_SET_SAME_PRESCRIPTION',
);

export const uploadFile = createAction('CONTACTS_WIZARD_UPLOAD_FILE');

/**
 * Prescription reducer
 */
export const prescription = createReducer<PrescriptionType>({}, initialState)
    /**
     * Subscribe actions
     * https://www.npmjs.com/package/redux-act#typescript
     */
    .on(
        changePrescriptionMethod,
        (state: PrescriptionType, payload: MethodType): PrescriptionType => ({
            ...state,
            method: payload,
            uploadedData: initialState.uploadedData,
        }),
    )
    .on(
        uploadFile,
        (state: PrescriptionType, payload: UploadedDataType): PrescriptionType => ({
            ...state,
            uploadedData: payload,
            method: UPLOAD,
        }),
    )
    .on(
        toggleSamePrescriptionForBothEyes,
        (state: PrescriptionType): PrescriptionType => ({
            ...state,
            isSamePrescriptionForBothEyes: !state.isSamePrescriptionForBothEyes,
        }),
    )
    .on(
        `${fetchQuoteItemV2}_${FULFILLED}`,
        (state: PrescriptionType, payload: AxiosResponse<OneQuoteItem>): PrescriptionType => {
            return {
                ...state,
                method: pathOr(SEND_LATER, ['prescription', 'method'], payload.data),
                uploadedData: {
                    ...state.uploadedData,
                    fileSrc: pathOr('', ['prescription', 'link'], payload.data),
                },
                isSamePrescriptionForBothEyes: equals(
                    omit(['boxes'], pathOr({}, ['prescription', 'od'], payload.data)),
                    omit(['boxes'], pathOr({}, ['prescription', 'os'], payload.data)),
                ),
            };
        },
    );

/**
 * Get is CL prescription the same for both eyes
 *
 * @param {ApplicationStoreType} store - redux store
 * @returns {boolean} Is same prescription for both eyes
 */
export const getIsSamePrescriptionForBothEyes = (store: ApplicationStoreType): boolean =>
    getContactsPdp(store).prescription.isSamePrescriptionForBothEyes;

/**
 * Get CL prescription
 *
 * @param {ApplicationStoreType} store - redux store
 * @returns {Prescription} CL prescription
 */
export const getPrescription = (store: ApplicationStoreType): PrescriptionType =>
    getContactsPdp(store).prescription;
